import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { CircularProgress, Tooltip } from '@mui/material';

import InputLabel from '../../../components/v2/InputLabel';
import ExternalCoachContext from '../../context/ExternalCoachContext';
import RadioButtonGroup from '../RadioButtonGroup';
import { Container, OptionLabel } from './styles';
import texts from './texts.json';

/**
 * Asks which of the client's products an assignment is for. Renders nothing below two live
 * products, and a loading state until their names arrive. Nothing is preselected.
 */
const AssignmentProductSelector = ({
  userDoc,
  selectedProductId,
  onSelect,
}) => {
  const { products, isProductsReady } = useContext(ExternalCoachContext);

  const productIds = userDoc?.activeProductIds ?? [];
  const options = productIds.length < 2 ? [] : productIds.map((productId) => {
    // Never the raw id: an unresolved product is a data gap, not something to show a coach.
    const name = products?.find(({ id }) => id === productId)?.name || texts.unnamedProduct;
    return {
      value: productId,
      label: (
        <Tooltip title={name} arrow>
          <OptionLabel>{name}</OptionLabel>
        </Tooltip>
      ),
    };
  });

  // A product the client doesn't hold leaves the group blank while reading as answered.
  const isForeignSelection = !!selectedProductId && !productIds.includes(selectedProductId);
  useEffect(() => {
    if (isForeignSelection) {
      onSelect(null);
    }
  }, [isForeignSelection, onSelect]);

  if (!options.length) {
    return null;
  }

  /*
    A spinner rather than nothing while the names load: the assign modals disable Save on
    `hasMultipleActiveProducts && !selectedProductId` without waiting for this readiness flag,
    so there has to be a control on screen explaining the disabled button. Mirrors the products
    cell in `UserTable`.
  */
  if (!isProductsReady) {
    return (
      <Container>
        <InputLabel>{texts.label}</InputLabel>
        <CircularProgress size={20} />
      </Container>
    );
  }

  return (
    <Container>
      <InputLabel>{texts.label}</InputLabel>
      <RadioButtonGroup
        options={options}
        selectedOption={selectedProductId || ''}
        onOptionChange={onSelect}
      />
    </Container>
  );
};

AssignmentProductSelector.propTypes = {
  userDoc: PropTypes.shape({
    activeProductIds: PropTypes.arrayOf(PropTypes.string),
  }),
  selectedProductId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

AssignmentProductSelector.defaultProps = {
  userDoc: null,
  selectedProductId: null,
};

export default observer(AssignmentProductSelector);
